import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton,
  Tooltip,
  Backdrop,
  Divider,
  Chip,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  DeleteForever as TrashIcon
} from '@mui/icons-material';
import { materialAPI, siteAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import BulkMaterialForm from '../components/BulkMaterialForm';
import MaterialSuggestField from '../components/MaterialSuggestField';
import EmployeeExpenseBulkForm from '../components/EmployeeExpenseBulkForm';
import VendorSummaryTab from '../components/VendorSummaryTab';
import VendorComparisonTab from '../components/VendorComparisonTab';
import BOQSummaryTab from '../components/BOQSummaryTab';
import VendorPaymentsTab from '../components/VendorPaymentsTab';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const DEFAULT_FIELD_SETTINGS = {
  materialItemMode: 'dropdown',
  formType: 'material',
  quantityRequired: true,
  unitRequired: true,
  supplierNameRequired: true,
  billNumberRequired: true,
  amountRequired: true,
  billUploadRequired: true,
  gstSlabRequired: true
};

const MaterialsPage = () => {
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [formMode, setFormMode] = useState('single');
  const [materialType, setMaterialType] = useState('received');
  const [bulkEntryType, setBulkEntryType] = useState('material'); // 'material' | 'expense'

  const [departments, setDepartments] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [materialItems, setMaterialItems] = useState([]);
  const [filteredExpenseCategories, setFilteredExpenseCategories] = useState([]);
  const [filteredMaterialItems, setFilteredMaterialItems] = useState([]);

  const [allFieldSettings, setAllFieldSettings] = useState({});

  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();

  const [billFile, setBillFile] = useState(null);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [eWayBillFile, setEWayBillFile] = useState(null);
  const [showCustomItemField, setShowCustomItemField] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [siteInventory, setSiteInventory] = useState([]);
  const [quickUseData, setQuickUseData] = useState({});
  const [quickUseConfirm, setQuickUseConfirm] = useState(null);

  // ✅ Edit, Delete, Trash states
  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashedEntries, setTrashedEntries] = useState([]);
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  const [pendingEntries, setPendingEntries] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingTypeFilter, setPendingTypeFilter] = useState('all');
  const [pendingAllSites, setPendingAllSites] = useState(false);
  const [sendingPendingEmail, setSendingPendingEmail] = useState(false);
  const [completeBillOpen, setCompleteBillOpen] = useState(false);
  const [completeBillEntry, setCompleteBillEntry] = useState(null);
  const [completeBillData, setCompleteBillData] = useState({});
  const [completeBillFile, setCompleteBillFile] = useState(null);
  const [completeBillEWayFile, setCompleteBillEWayFile] = useState(null);
  const [completeBillSubmitting, setCompleteBillSubmitting] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseType, setResponseType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [entryTypeFilter, setEntryTypeFilter] = useState('all'); // 'all' | 'material' | 'expense'
  const [quickUseSearch, setQuickUseSearch] = useState('');
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [allVendorNamesForEntry, setAllVendorNamesForEntry] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/vendors/all-names`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => setAllVendorNamesForEntry(res.data.names || []))
      .catch(() => { });
  }, []);

  const [formData, setFormData] = useState({
    siteId: '',
    departmentId: '',
    expenseCategoryId: '',
    materialItemId: '',
    customItemDescription: '',
    employeeName: '',
    billNumber: '',
    billType: 'GST',
    billStatus: 'received',
    billPendingFrom: '',
    gstPercentage: '',
    paymentSource: 'COMPANY',
    remarks: '',
    categoryId: '',
    quantity: '',
    unit: '',
    supplierName: '',
    amount: '',
    usageDescription: '',
    entryDate: new Date().toISOString().split('T')[0]
  });

  const [items, setItems] = useState([]);

  const currentFieldSettings = formData.expenseCategoryId
    ? (allFieldSettings[parseInt(formData.expenseCategoryId)] || DEFAULT_FIELD_SETTINGS)
    : DEFAULT_FIELD_SETTINGS;
  const isEmployeeExpenseForm = materialType === 'received' && currentFieldSettings.formType === 'employee_expense';

  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    if (formData.siteId) fetchPendingEntries(formData.siteId);
  }, [pendingAllSites]);

  useEffect(() => {
    if (sites.length > 0 && sites[0].id) {
      fetchEntries(sites[0].id);
      fetchSiteInventory(sites[0].id);
      fetchPendingEntries(sites[0].id);
    }
  }, [sites]);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [categoriesRes, sitesRes, departmentsRes, expenseCategoriesRes, materialItemsRes, fieldSettingsRes] = await Promise.all([
        materialAPI.getCategories(),
        siteAPI.getAll(),
        axios.get(`${API_BASE_URL}/departments`, { headers }),
        axios.get(`${API_BASE_URL}/expense-categories`, { headers }),
        axios.get(`${API_BASE_URL}/material-items`, { headers }),
        axios.get(`${API_BASE_URL}/expense-categories/field-settings/all`, { headers })
      ]);

      setCategories(categoriesRes.data.data.categories);
      setSites(sitesRes.data.data.sites);
      setDepartments(departmentsRes.data.data);
      setExpenseCategories(expenseCategoriesRes.data.data);
      setMaterialItems(materialItemsRes.data.data);

      const settingsMap = {};
      fieldSettingsRes.data.data.forEach(item => {
        settingsMap[item.expenseCategoryId] = item.settings;
      });
      setAllFieldSettings(settingsMap);

      if (sitesRes.data.data.sites.length > 0) {
        setFormData(prev => ({ ...prev, siteId: sitesRes.data.data.sites[0].id }));
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    }
  };

  const fetchEntries = async (siteId) => {
    try {
      setLoading(true);
      const response = await materialAPI.getBySite(siteId);
      setEntries(response.data.data.entries);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteInventory = async (siteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/inventory?siteId=${siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSiteInventory(response.data.data.inventory || []);
    } catch (err) {
      console.error('Failed to fetch site inventory:', err);
    }
  };

  const fetchPendingEntries = async (siteId) => {
    if (!siteId) return;
    try {
      setPendingLoading(true);
      const token = localStorage.getItem('token');
      const url = pendingAllSites
        ? `${API_BASE_URL}/materials?billStatus=pending`
        : `${API_BASE_URL}/materials/site/${siteId}?billStatus=pending`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setPendingEntries(response.data.data.entries.filter(e => e.entryType === 'received' && !e.employeeName));
    } catch (err) {
      console.error('Failed to fetch pending entries:', err);
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchTrashedEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/materials/trash?siteId=${formData.siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrashedEntries(response.data.data.entries);
    } catch (err) {
      console.error('Failed to fetch trash:', err);
    }
  };

  const fetchVendorSuggestions = async (siteId, material) => {
    if (!siteId || !material) { setVendorSuggestions([]); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE_URL}/vendor-comparisons/vendors?siteId=${siteId}&material=${encodeURIComponent(material)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVendorSuggestions(res.data.data.vendors || []);
    } catch (e) { setVendorSuggestions([]); }
  };

  const handleSendPendingBillsEmail = async () => {
    try {
      setSendingPendingEmail(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/materials/pending-bills/trigger-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(response.data.message || 'Pending bills email sent to managers!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send pending bills email');
    } finally {
      setSendingPendingEmail(false);
    }
  };

  const handleDownloadPendingBills = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/materials/pending-bills/export?siteId=${formData.siteId}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending_bills_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download pending bills report');
    }
  };

  const handleCompleteBillOpen = (entry) => {
    setCompleteBillEntry(entry);
    setCompleteBillData({
      billNumber: '',
      billType: 'GST',
      amount: entry.baseAmount || entry.amount || '',
      gstPercentage: ''
    });
    setCompleteBillFile(null);
    setCompleteBillEWayFile(null);
    setCompleteBillOpen(true);
  };

  const handleCompleteBillSubmit = async () => {
    try {
      setCompleteBillSubmitting(true);
      const token = localStorage.getItem('token');
      const isExpense = !!completeBillEntry.employeeName;

      const fd = new FormData();
      if (completeBillData.billNumber) fd.append('billNumber', completeBillData.billNumber);
      if (!isExpense) {
        fd.append('billType', completeBillData.billType);
        if (completeBillData.billType === 'GST' && completeBillData.gstPercentage) {
          fd.append('gstPercentage', completeBillData.gstPercentage);
        }
      }
      if (completeBillData.amount) fd.append('amount', completeBillData.amount);
      if (completeBillFile) fd.append('billAttachment', completeBillFile);
      if (completeBillEWayFile) fd.append('eWayBill', completeBillEWayFile);

      await axios.put(
        `${API_BASE_URL}/materials/${completeBillEntry.id}/complete-bill`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setCompleteBillOpen(false);
      setSuccess('Bill attached successfully!');
      fetchEntries(formData.siteId);
      fetchPendingEntries(formData.siteId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to attach bill');
    } finally {
      setCompleteBillSubmitting(false);
    }
  };

  const handleEditOpen = (entry) => {
    setEditEntry(entry);
    setEditFormData({
      entryDate: entry.entryDate,
      materialItemId: entry.materialItemId || null,
      customItemDescription: entry.customItemDescription || entry.materialItem?.item_name || entry.materialItem?.itemName || '',
      quantity: entry.quantity || '',
      unit: entry.unit || '',
      supplierName: entry.supplierName || '',
      billNumber: entry.billNumber || '',
      billType: entry.billType || 'NON_GST',
      amount: entry.baseAmount || entry.amount || '',
      gstPercentage: entry.gstPercentage || '',
      paymentSource: entry.paymentSource || 'COMPANY',
      remarks: entry.remarks || '',
      usageDescription: entry.usageDescription || ''
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');

      if (editFormData.newBillFile) {
        const formDataToSend = new FormData();
        Object.keys(editFormData).forEach(key => {
          if (key !== 'newBillFile' && editFormData[key] !== undefined && editFormData[key] !== null) {
            formDataToSend.append(key, editFormData[key]);
          }
        });
        formDataToSend.append('billAttachment', editFormData.newBillFile);
        if (editFormData.newEWayBillFile) formDataToSend.append('eWayBill', editFormData.newEWayBillFile);
        await axios.put(
          `${API_BASE_URL}/materials/${editEntry.id}`,
          formDataToSend,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
      } else if (editFormData.newEWayBillFile) {
        const formDataToSend = new FormData();
        Object.keys(editFormData).forEach(key => {
          if (key !== 'newBillFile' && key !== 'newEWayBillFile' && editFormData[key] !== undefined && editFormData[key] !== null) {
            formDataToSend.append(key, editFormData[key]);
          }
        });
        formDataToSend.append('eWayBill', editFormData.newEWayBillFile);
        await axios.put(
          `${API_BASE_URL}/materials/${editEntry.id}`,
          formDataToSend,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        await axios.put(
          `${API_BASE_URL}/materials/${editEntry.id}`,
          editFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setEditOpen(false);
      setSuccess('Entry updated successfully!');
      fetchEntries(formData.siteId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (type = 'soft') => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/materials/${entryToDelete.id}?type=${type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteDialogOpen(false);
      setSuccess(type === 'hard' ? 'Entry permanently deleted!' : 'Entry moved to trash!');
      fetchEntries(formData.siteId);
      if (showTrash) fetchTrashedEntries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete entry');
    }
  };

  const handleRestore = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/materials/${entryId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Entry restored successfully!');
      fetchTrashedEntries();
      fetchEntries(formData.siteId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore entry');
    }
  };

  const handleToggleTrash = () => {
    if (!showTrash) fetchTrashedEntries();
    setShowTrash(!showTrash);
  };

  const handleQuickUse = (inventoryItem) => {
    const qty = quickUseData[inventoryItem.materialId];
    if (!qty || parseFloat(qty) <= 0) {
      setError('Please enter a valid quantity');
      return;
    }
    if (parseFloat(qty) > inventoryItem.currentStock) {
      setError(`Cannot exceed available stock. Maximum allowed: ${inventoryItem.currentStock} ${inventoryItem.unit}`);
      return;
    }
    setQuickUseConfirm({ inventoryItem, quantity: parseFloat(qty) });
  };

  const handleQuickUseSubmit = async () => {
    if (!quickUseConfirm) return;
    try {
      const token = localStorage.getItem('token');
      const { inventoryItem, quantity } = quickUseConfirm;
      await axios.post(
        `${API_BASE_URL}/materials/log-used`,
        {
          siteId: formData.siteId,
          materialItemId: inventoryItem.type === 'item' ? inventoryItem.materialId : null,
          categoryId: inventoryItem.type === 'category' ? inventoryItem.materialId : null,
          customItemDescription: inventoryItem.type === 'custom' ? inventoryItem.materialName : null,
          quantity: quantity,
          unit: inventoryItem.unit,
          usageDescription: `Quick use - ${inventoryItem.materialName}`,
          entryDate: new Date().toISOString().split('T')[0]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuickUseConfirm(null);
      setQuickUseData({});
      setSuccess(`${quantity} ${inventoryItem.unit} of ${inventoryItem.materialName} logged as used!`);
      fetchEntries(formData.siteId);
      fetchSiteInventory(formData.siteId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to log usage');
    }
  };

  const handleOpen = (type) => {
    setMaterialType(type);
    setFormMode('single');
    setBulkEntryType('material');
    setFormData({
      ...formData,
      departmentId: '',
      expenseCategoryId: '',
      materialItemId: '',
      customItemDescription: '',
      employeeName: '',
      quantity: '',
      unit: '',
      supplierName: '',
      billNumber: '',
      amount: '',
      billType: 'GST',
      billStatus: 'received',
      billPendingFrom: '',
      gstPercentage: '',
      paymentSource: 'COMPANY',
      remarks: '',
      usageDescription: ''
    });
    setBillFile(null);
    setPaymentProofFile(null);
    setEWayBillFile(null);
    setFilteredExpenseCategories([]);
    setFilteredMaterialItems([]);
    setShowCustomItemField(false);
    setItems([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
    setFormMode('single');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');

      if (materialType === 'received') {
        const useBulk = formMode === 'bulk' && items && items.length > 0;
        if (useBulk) {
          if (bulkEntryType === 'expense') {
            await axios.post(`${API_BASE_URL}/materials/log-received-bulk-expense`, {
              siteId: formData.siteId,
              departmentId: formData.departmentId,
              items: items.map(item => ({
                employeeName: item.employeeName,
                expenseCategoryId: item.expenseCategoryId,
                amount: item.amount,
                paymentSource: item.paymentSource || 'COMPANY',
                remarks: item.remarks || null,
                entryDate: item.entryDate,
                billStatus: item.billStatus || 'received',
                billPendingFrom: item.billPendingFrom || null
              }))
            }, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            setResponseType('success');
            setResponseMessage(`${items.length} expense entr${items.length === 1 ? 'y' : 'ies'} logged successfully!`);
            setResponseDialogOpen(true);
            fetchEntries(formData.siteId);
            setIsSubmitting(false);
            setLoading(false);
            return;
          }
          const formDataToSend = new FormData();
          formDataToSend.append('siteId', formData.siteId);
          formDataToSend.append('departmentId', formData.departmentId);
          formDataToSend.append('supplierName', formData.supplierName);
          formDataToSend.append('billStatus', formData.billStatus || 'received');
          if (formData.billStatus === 'pending') {
            formDataToSend.append('billPendingFrom', formData.billPendingFrom || '');
          } else {
            formDataToSend.append('billNumber', formData.billNumber);
            formDataToSend.append('billType', formData.billType);
          }
          formDataToSend.append('paymentSource', formData.paymentSource);
          formDataToSend.append('entryDate', formData.entryDate);
          if (formData.remarks) formDataToSend.append('remarks', formData.remarks);
          formDataToSend.append('items', JSON.stringify(items.map(item => ({
            expenseCategoryId: item.expenseCategoryId,
            materialItemId: item.materialItemId,
            customItemDescription: item.customItemDescription,
            quantity: item.quantity,
            unit: item.unit,
            amount: item.amount,
            gstPercentage: item.gstPercentage || null
          }))));
          if (billFile) formDataToSend.append('billAttachment', billFile);
          if (eWayBillFile) formDataToSend.append('eWayBill', eWayBillFile);
          if (paymentProofFile && formData.paymentSource === 'SUPERVISOR') {
            formDataToSend.append('paymentProof', paymentProofFile);
          }
          await axios.post(`${API_BASE_URL}/materials/log-received-bulk`, formDataToSend, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
          setResponseType('success');
          setResponseMessage(`${items.length} material(s) logged successfully!`);
          setResponseDialogOpen(true);
        } else {
          const formDataToSend = new FormData();
          formDataToSend.append('siteId', formData.siteId);
          formDataToSend.append('quantity', formData.quantity);
          formDataToSend.append('unit', formData.unit);
          formDataToSend.append('entryDate', formData.entryDate);
          if (formData.departmentId) formDataToSend.append('departmentId', formData.departmentId);
          if (formData.expenseCategoryId) formDataToSend.append('expenseCategoryId', formData.expenseCategoryId);
          if (formData.materialItemId) formDataToSend.append('materialItemId', formData.materialItemId);
          if (formData.customItemDescription) formDataToSend.append('customItemDescription', formData.customItemDescription);
          if (formData.employeeName) formDataToSend.append('employeeName', formData.employeeName);
          formDataToSend.append('supplierName', formData.supplierName);
          formDataToSend.append('billStatus', formData.billStatus);
          if (formData.billStatus === 'pending') {
            formDataToSend.append('billPendingFrom', formData.billPendingFrom);
          } else {
            formDataToSend.append('billNumber', formData.billNumber);
            formDataToSend.append('billType', formData.billType);
          }
          formDataToSend.append('amount', formData.amount);
          if (formData.billType === 'GST' && formData.gstPercentage) {
            formDataToSend.append('gstPercentage', formData.gstPercentage);
          }
          formDataToSend.append('paymentSource', formData.paymentSource);
          if (formData.remarks) formDataToSend.append('remarks', formData.remarks);
          if (billFile) formDataToSend.append('billAttachment', billFile);
          if (eWayBillFile) formDataToSend.append('eWayBill', eWayBillFile);
          if (paymentProofFile && formData.paymentSource === 'SUPERVISOR') {
            formDataToSend.append('paymentProof', paymentProofFile);
          }
          await axios.post(`${API_BASE_URL}/materials/log-received`, formDataToSend, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
          setResponseType('success');
          setResponseMessage('Material received logged successfully!');
          setResponseDialogOpen(true);
        }
        fetchEntries(formData.siteId);
      } else {
        await axios.post(
          `${API_BASE_URL}/materials/log-used`,
          {
            siteId: formData.siteId,
            departmentId: formData.departmentId,
            expenseCategoryId: formData.expenseCategoryId,
            materialItemId: formData.materialItemId,
            quantity: formData.quantity,
            unit: formData.unit,
            usageDescription: formData.usageDescription,
            entryDate: formData.entryDate
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setResponseType('success');
        setResponseMessage('Material usage logged successfully!');
        setResponseDialogOpen(true);
        fetchEntries(formData.siteId);
      }
    } catch (err) {
      console.error('Submit error:', err);
      setResponseType('error');
      setResponseMessage(err.response?.data?.message || 'Failed to log material. Please try again.');
      setResponseDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResponseDialogClose = () => {
    setResponseDialogOpen(false);
    setResponseMessage('');
    setResponseType('');
    setIsSubmitting(false);
    if (responseType === 'success') handleClose();
  };

  const handleViewBill = (billUrl) => {
    setSelectedImage(billUrl);
    setImageViewerOpen(true);
  };

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
    setSelectedImage(null);
  };

  const handleDownloadImage = () => {
    if (selectedImage) window.open(selectedImage, '_blank');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'departmentId') {
      setFilteredExpenseCategories(expenseCategories);
      setFormData(prev => ({ ...prev, departmentId: value, expenseCategoryId: '', materialItemId: '', unit: '' }));
      setFilteredMaterialItems([]);
    }

    if (name === 'expenseCategoryId') {
      const selectedCategory = expenseCategories.find(cat => cat.id === parseInt(value));
      const isOtherMisc = selectedCategory?.category_name?.toLowerCase().includes('miscellaneous') ||
        selectedCategory?.category_name?.toLowerCase().includes('other');
      setShowCustomItemField(isOtherMisc);

      const filtered = materialItems.filter(item =>
        item.departmentId === parseInt(formData.departmentId) &&
        item.expenseCategoryId === parseInt(value)
      );
      setFilteredMaterialItems(filtered);
      setFormData(prev => ({ ...prev, expenseCategoryId: value, materialItemId: '', customItemDescription: '', unit: '' }));
    }

    if (name === 'materialItemId') {
      const item = materialItems.find(i => i.id === parseInt(value));
      if (item) setFormData(prev => ({ ...prev, unit: item.unit || '' }));
    }

    if (name === 'materialItemId') {
      const item = materialItems.find(i => i.id === parseInt(value));
      if (item) {
        // Strip unit suffix like " ( MT )" or " (KG)" from item name for better matching
        const cleanName = item.itemName.replace(/\s*\(.*?\)\s*/g, '').trim();
        fetchVendorSuggestions(formData.siteId, cleanName);
      }
    }

    if (name === 'categoryId') {
      const category = categories.find(c => c.id === value);
      if (category) setFormData(prev => ({ ...prev, unit: category.unit }));
    }

    if (name === 'billType') {
      setFormData(prev => ({ ...prev, billType: value, gstPercentage: '' }));
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === 'bill') setBillFile(file);
      else if (fileType === 'payment') setPaymentProofFile(file);
      else if (fileType === 'eWayBill') setEWayBillFile(file);
    }
  };

  const filteredEntries = entries.filter(entry => {
    const matchesTab = tab === 0 ? entry.entryType === 'received' : entry.entryType === 'used';
    if (!matchesTab) return false;
    if (entryTypeFilter === 'material' && entry.employeeName) return false;
    if (entryTypeFilter === 'expense' && !entry.employeeName) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      entry.materialItem?.itemName?.toLowerCase().includes(q) ||
      entry.category?.categoryName?.toLowerCase().includes(q) ||
      entry.customItemDescription?.toLowerCase().includes(q) ||
      entry.expenseCategory?.category_name?.toLowerCase().includes(q) ||
      entry.employeeName?.toLowerCase().includes(q) ||
      entry.supplierName?.toLowerCase().includes(q) ||
      entry.billNumber?.toLowerCase().includes(q) ||
      (tab === 1 && entry.usageDescription?.toLowerCase().includes(q)) ||
      String(entry.amount || '').includes(q) ||
      entry.unit?.toLowerCase().includes(q) ||
      new Date(entry.entryDate).toLocaleDateString().includes(q)
    );
  });

  const renderMaterialItemField = () => {
    const mode = currentFieldSettings.materialItemMode;

    if (mode === 'hidden') return null;

    if (mode === 'both') {
      return (
        <>
          <Grid item xs={12}>
            <Autocomplete fullWidth options={filteredMaterialItems}
              getOptionLabel={(option) => option.itemName}
              value={filteredMaterialItems.find(item => item.id === formData.materialItemId) || null}
              onChange={(event, newValue) => {
                handleChange({ target: { name: 'materialItemId', value: newValue ? newValue.id : '' } });
              }}
              disabled={!formData.expenseCategoryId}
              renderInput={(params) => (
                <TextField {...params} label="Material Item (Optional)"
                  helperText={!formData.expenseCategoryId ? "Select Department and Expense Category first" : "Type to search or enter custom description below"} />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
          <Grid item xs={12}>
            <MaterialSuggestField
              label="Or type custom description"
              value={formData.customItemDescription}
              onChange={(text) => setFormData(prev => ({ ...prev, customItemDescription: text }))}
              onSelectExisting={(item) => {
                setMaterialItems(prev => prev.some(mi => mi.id === item.id) ? prev : [...prev, item]);
                setFilteredMaterialItems(prev => prev.some(mi => mi.id === item.id) ? prev : [...prev, item]);
                setFormData(prev => ({
                  ...prev,
                  materialItemId: item.id,
                  customItemDescription: '',
                  unit: item.unit || prev.unit
                }));
              }}
              departmentId={formData.departmentId}
              expenseCategoryId={formData.expenseCategoryId}
              placeholder="Type material name if not in dropdown above..."
              helperText="Use this if item is not available in the dropdown"
            />
          </Grid>
        </>
      );
    }

    return (
      <>
        <Grid item xs={12}>
          <Autocomplete fullWidth options={filteredMaterialItems}
            getOptionLabel={(option) => option.itemName}
            value={filteredMaterialItems.find(item => item.id === formData.materialItemId) || null}
            onChange={(event, newValue) => {
              handleChange({ target: { name: 'materialItemId', value: newValue ? newValue.id : '' } });
            }}
            disabled={!formData.expenseCategoryId}
            renderInput={(params) => (
              <TextField {...params} label="Material Item" required={false}
                helperText={!formData.expenseCategoryId ? "Select Department and Expense Category first" : "Type to search..."} />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>
        {showCustomItemField && (
          <Grid item xs={12}>
            <MaterialSuggestField
              label="Custom Item Description"
              multiline
              value={formData.customItemDescription}
              onChange={(text) => setFormData(prev => ({ ...prev, customItemDescription: text }))}
              onSelectExisting={(item) => {
                setMaterialItems(prev => prev.some(mi => mi.id === item.id) ? prev : [...prev, item]);
                setFilteredMaterialItems(prev => prev.some(mi => mi.id === item.id) ? prev : [...prev, item]);
                setFormData(prev => ({
                  ...prev,
                  materialItemId: item.id,
                  customItemDescription: '',
                  unit: item.unit || prev.unit
                }));
              }}
              departmentId={formData.departmentId}
              expenseCategoryId={formData.expenseCategoryId}
              placeholder="Describe the miscellaneous item..."
              helperText="Admin will review and may create a proper item from this"
            />
          </Grid>
        )}
      </>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Materials Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen('received')}>
            + LOG RECEIVED
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpen('used')}>
            + LOG USED
          </Button>
          {isAdmin && (
            <Button variant="outlined" color={showTrash ? 'error' : 'inherit'}
              startIcon={<TrashIcon />} onClick={handleToggleTrash}>
              {showTrash ? 'Hide Trash' : 'Trash'}
            </Button>
          )}
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Site Selector */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField select fullWidth label="Select Site" value={formData.siteId}
          onChange={(e) => {
            setFormData(prev => ({ ...prev, siteId: e.target.value }));
            fetchEntries(e.target.value);
            fetchSiteInventory(e.target.value);
            fetchPendingEntries(e.target.value);
          }}>
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>{site.siteName} - {site.location}</MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* Tabs + Search */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1 }}>
          <Tabs value={tab} onChange={(e, newValue) => { setTab(newValue); setSearchQuery(''); }}>
            <Tab label="Materials Received" />
            <Tab label="Materials Used" />
            <Tab label={`Pending Bills${pendingEntries.length > 0 ? ` (${pendingEntries.length})` : ''}`} />
            <Tab label="Vendor Summary" />
            <Tab label="BOQ Summary" />
            <Tab label="Vendor Comparison" />
            <Tab label="Payments" />
          </Tabs>
          {tab < 2 && (
            <TextField
              select size="small" label="Type" value={entryTypeFilter}
              onChange={(e) => setEntryTypeFilter(e.target.value)}
              sx={{ width: 160, mr: 1 }}
            >
              <MenuItem value="all">All Entries</MenuItem>
              <MenuItem value="material">Materials Only</MenuItem>
              <MenuItem value="expense">Employee Expenses Only</MenuItem>
            </TextField>
          )}
          {tab < 2 && (
            <TextField
              size="small"
              placeholder="Search materials, supplier, bill no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Entries Table */}
      {tab < 2 && <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Material</TableCell>
              <TableCell>Quantity</TableCell>
              {tab === 0 ? (
                <>
                  <TableCell>Description</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Bill Number</TableCell>
                  <TableCell align="center">Bill Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Bill</TableCell>
                  <TableCell align="center">E-Way Bill</TableCell>
                  {(isAdmin || isManager) && <TableCell align="center">Actions</TableCell>}
                </>
              ) : (
                <>
                  <TableCell>Usage Description</TableCell>
                  {(isAdmin || isManager) && <TableCell align="center">Actions</TableCell>}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tab === 0 ? 11 : 6} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : filteredEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tab === 0 ? 11 : 6} align="center">No entries found</TableCell>
              </TableRow>
            ) : (
              filteredEntries.map((entry) => (
                <TableRow key={entry.id}
                  sx={(!entry.employeeName && !/imprest for site (expense|visit)/i.test(entry.customItemDescription || entry.materialItem?.item_name || '') && entry.billStatus === 'pending') ? { bgcolor: '#fff8e1' } : undefined}>
                  <TableCell>{new Date(entry.entryDate).toLocaleDateString()}</TableCell>
                  <TableCell>{entry.category?.categoryName || entry.materialItem?.item_name || entry.customItemDescription || entry.expenseCategory?.category_name || '-'}</TableCell>
                  <TableCell>{entry.quantity} {entry.unit}</TableCell>
                  {tab === 0 ? (
                    <>
                      <TableCell>
                        {entry.usageDescription ? (
                          <Typography variant="body2"
                            sx={{ color: entry.usageDescription.includes('Transfer') ? 'primary.main' : 'text.primary', fontWeight: entry.usageDescription.includes('Transfer') ? 500 : 400 }}>
                            {entry.usageDescription}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{entry.supplierName || entry.employeeName || '-'}</TableCell>
                      <TableCell>{entry.billNumber || '-'}</TableCell>
                      <TableCell align="center">
                        {(entry.employeeName || /imprest for site (expense|visit)/i.test(entry.customItemDescription || entry.materialItem?.item_name || '')) ? (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        ) : entry.billStatus === 'pending' ? (
                          <Chip label={`Pending${entry.billPendingFrom ? ` (${entry.billPendingFrom})` : ''}`}
                            size="small" color="warning" />
                        ) : (entry.billNumber || entry.billAttachment) ? (
                          <Chip label="Received" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="No Bill" size="small" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'divider' }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        ₹{Number(entry.amount).toLocaleString()}
                        {entry.gstPercentage && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            incl. {entry.gstPercentage}% GST
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entry.billAttachment ? (
                          <Tooltip title="View Bill">
                            <IconButton size="small" color="primary" onClick={() => handleViewBill(entry.billAttachment)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entry.eWayBill ? (
                          <Tooltip title="View E-Way Bill">
                            <IconButton size="small" color="secondary" onClick={() => handleViewBill(entry.eWayBill)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      {(isAdmin || isManager) && (
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleEditOpen(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(entry)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </>
                  ) : (
                    <>
                      <TableCell>
                        {entry.usageDescription ? (
                          <Typography variant="body2"
                            sx={{ color: entry.usageDescription.includes('Transfer') ? 'error.main' : 'text.primary', fontWeight: entry.usageDescription.includes('Transfer') ? 500 : 400 }}>
                            {entry.usageDescription}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      {(isAdmin || isManager) && (
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleEditOpen(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(entry)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>}

      {/* Trash Table */}
      {tab < 2 && showTrash && isAdmin && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>🗑️ Deleted Entries (Trash)</Typography>
          <TableContainer component={Paper} sx={{ border: '2px solid #ffcdd2' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#ffebee' }}>
                <TableRow>
                  <TableCell>Deleted At</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="center">Restore</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trashedEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No deleted entries</TableCell>
                  </TableRow>
                ) : (
                  trashedEntries.map((entry) => (
                    <TableRow key={entry.id} sx={{ bgcolor: '#fff8f8' }}>
                      <TableCell>
                        <Typography variant="caption" color="error">
                          {new Date(entry.deletedAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                      <TableCell>{entry.siteName}</TableCell>
                      <TableCell>{entry.materialName}</TableCell>
                      <TableCell>
                        <Chip label={entry.entryType} size="small"
                          color={entry.entryType === 'received' ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell align="right">₹{Number(entry.amount || 0).toLocaleString()}</TableCell>
                      <TableCell>{entry.supplierName || entry.employeeName || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Restore Entry">
                          <IconButton size="small" color="success" onClick={() => handleRestore(entry.id)}>
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Quick Use Table — shown in Materials Used tab */}
      {tab === 1 && siteInventory.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            ⚡ Quick Log Usage
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Enter quantity used for any material and click Log Use
            </Typography>
            <TextField
              size="small"
              placeholder="Search material..."
              value={quickUseSearch}
              onChange={(e) => setQuickUseSearch(e.target.value)}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: quickUseSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQuickUseSearch('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Material</strong></TableCell>
                  <TableCell align="right"><strong>Received</strong></TableCell>
                  <TableCell align="right"><strong>Used</strong></TableCell>
                  <TableCell align="right"><strong>Available Stock</strong></TableCell>
                  <TableCell align="center"><strong>Qty to Use</strong></TableCell>
                  <TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {siteInventory.filter(i => i.currentStock > 0 && (
                  !quickUseSearch.trim() ||
                  i.materialName?.toLowerCase().includes(quickUseSearch.toLowerCase()) ||
                  i.unit?.toLowerCase().includes(quickUseSearch.toLowerCase())
                )).map((item) => (
                  <TableRow key={item.materialId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{item.materialName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.unit}</Typography>
                    </TableCell>
                    <TableCell align="right">{item.received} {item.unit}</TableCell>
                    <TableCell align="right">{item.used} {item.unit}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {item.currentStock} {item.unit}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 150 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={quickUseData[item.materialId] || ''}
                        onChange={(e) => setQuickUseData(prev => ({ ...prev, [item.materialId]: e.target.value }))}
                        inputProps={{ min: 0.01, step: 0.01, max: item.currentStock }}
                        placeholder="0"
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => handleQuickUse(item)}
                        disabled={!quickUseData[item.materialId]}
                      >
                        Log Use
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Quick Use Confirm Dialog */}
      <Dialog open={!!quickUseConfirm} onClose={() => setQuickUseConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Material Usage</DialogTitle>
        <DialogContent>
          {quickUseConfirm && (
            <Box sx={{ mt: 1 }}>
              <Alert severity={quickUseConfirm.quantity > quickUseConfirm.inventoryItem.currentStock ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {quickUseConfirm.quantity > quickUseConfirm.inventoryItem.currentStock
                  ? `Warning: Quantity (${quickUseConfirm.quantity}) exceeds available stock (${quickUseConfirm.inventoryItem.currentStock})`
                  : 'Please confirm the usage entry'}
              </Alert>
              <Typography><strong>Material:</strong> {quickUseConfirm.inventoryItem.materialName}</Typography>
              <Typography><strong>Quantity to Log:</strong> {quickUseConfirm.quantity} {quickUseConfirm.inventoryItem.unit}</Typography>
              <Typography><strong>Available Stock:</strong> {quickUseConfirm.inventoryItem.currentStock} {quickUseConfirm.inventoryItem.unit}</Typography>
              <Typography><strong>Date:</strong> {new Date().toLocaleDateString()}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickUseConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleQuickUseSubmit}>
            Confirm Log Usage
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pending Bills Tab */}
      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Entries Awaiting Bill {pendingAllSites ? '— All Sites' : `— ${sites.find(s => s.id === formData.siteId)?.siteName || ''}`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={pendingAllSites} onChange={(e) => setPendingAllSites(e.target.checked)} size="small" />}
                label="All Sites"
              />
              <TextField select size="small" label="Type" value={pendingTypeFilter}
                onChange={(e) => setPendingTypeFilter(e.target.value)} sx={{ width: 200 }}>
                <MenuItem value="all">All Entries</MenuItem>
                <MenuItem value="material">Materials Only</MenuItem>
                <MenuItem value="expense">Employee Expenses Only</MenuItem>
              </TextField>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadPendingBills}>
                Download Report
              </Button>
              {(isAdmin || isManager) && (
                <Button variant="contained" color="warning" onClick={handleSendPendingBillsEmail} disabled={sendingPendingEmail}>
                  {sendingPendingEmail ? 'Sending...' : 'Send Alert Email Now'}
                </Button>
              )}
            </Box>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {pendingAllSites && <TableCell>Site</TableCell>}
                  <TableCell>Date</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Pending From</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingLoading ? (
                  <TableRow><TableCell colSpan={pendingAllSites ? 8 : 7} align="center"><CircularProgress /></TableCell></TableRow>
                ) : pendingEntries.filter(e => {
                  if (pendingTypeFilter === 'material') return !e.employeeName;
                  if (pendingTypeFilter === 'expense') return !!e.employeeName;
                  return true;
                }).length === 0 ? (
                  <TableRow><TableCell colSpan={pendingAllSites ? 8 : 7} align="center">No entries with pending bills 🎉</TableCell></TableRow>
                ) : (
                  pendingEntries.filter(e => {
                    if (pendingTypeFilter === 'material') return !e.employeeName;
                    if (pendingTypeFilter === 'expense') return !!e.employeeName;
                    return true;
                  }).map((entry) => (
                    <TableRow key={entry.id} sx={{ bgcolor: '#fff8e1' }}>
                      {pendingAllSites && <TableCell>{entry.site?.siteName || '-'}</TableCell>}
                      <TableCell>{new Date(entry.entryDate).toLocaleDateString()}</TableCell>
                      <TableCell>{entry.category?.categoryName || entry.materialItem?.item_name || entry.customItemDescription || entry.expenseCategory?.category_name || '-'}</TableCell>
                      <TableCell>{entry.quantity ? `${entry.quantity} ${entry.unit}` : '-'}</TableCell>
                      <TableCell>{entry.supplierName || entry.employeeName || '-'}</TableCell>
                      <TableCell>{entry.billPendingFrom || '-'}</TableCell>
                      <TableCell align="right">₹{Number(entry.amount).toLocaleString()}</TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="contained" color="warning" onClick={() => handleCompleteBillOpen(entry)}>
                          Add Bill
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Vendor Summary Tab */}
      {tab === 3 && (
        <Paper sx={{ p: 3, mt: 0 }}>
          <VendorSummaryTab
            selectedSiteId={formData.siteId}
            sites={sites}
          />
        </Paper>
      )}

      {/* BOQ Summary Tab */}
      {tab === 4 && (
        <Paper sx={{ p: 3, mt: 0 }}>
          <BOQSummaryTab
            selectedSiteId={formData.siteId}
            sites={sites}
            userRole={user?.role}
          />
        </Paper>
      )}

      {/* Vendor Comparison Tab */}
      {tab === 5 && (
        <Paper sx={{ p: 3, mt: 0 }}>
          <VendorComparisonTab
            selectedSiteId={formData.siteId}
            sites={sites}
            userRole={user?.role}
          />
        </Paper>
      )}

      {tab === 6 && (
        <Paper sx={{ p: 3, mt: 0 }}>
          <VendorPaymentsTab
            selectedSiteId={formData.siteId}
            sites={sites}
            userRole={user?.role}
          />
        </Paper>
      )}

      {/* material dialogue */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Log Material {materialType === 'received' ? 'Received' : 'Used'}</DialogTitle>

          {materialType === 'received' && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
              <Tabs value={formMode} onChange={(e, newValue) => setFormMode(newValue)}>
                <Tab label="Single Item" value="single" />
                <Tab label="Multiple Items (Bulk)" value="bulk" />
              </Tabs>
            </Box>
          )}

          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {formMode === 'bulk' && materialType === 'received' ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Tabs value={bulkEntryType} onChange={(e, v) => setBulkEntryType(v)}>
                    <Tab label="Material Items" value="material" />
                    <Tab label="Employee Expenses (Food/Imprest)" value="expense" />
                  </Tabs>
                </Box>
                {bulkEntryType === 'material' ? (
                  <BulkMaterialForm
                    formData={formData}
                    sites={sites}
                    departments={departments}
                    expenseCategories={expenseCategories}
                    materialItems={materialItems}
                    filteredExpenseCategories={filteredExpenseCategories}
                    filteredMaterialItems={filteredMaterialItems}
                    billFile={billFile}
                    paymentProofFile={paymentProofFile}
                    eWayBillFile={eWayBillFile}
                    onItemsChange={(updatedItems) => {
                      setItems(updatedItems);
                      setFormData(prev => ({ ...prev, bulkItems: updatedItems }));
                    }}
                    onFormDataChange={(e) => {
                      const { name, value } = e.target;
                      setFormData(prev => ({ ...prev, [name]: value }));
                    }}
                    onFileChange={handleFileChange}
                    setFilteredExpenseCategories={setFilteredExpenseCategories}
                    setFilteredMaterialItems={setFilteredMaterialItems}
                    allFieldSettings={allFieldSettings}
                  />
                ) : (
                  <EmployeeExpenseBulkForm
                    formData={formData}
                    sites={sites}
                    departments={departments}
                    expenseCategories={expenseCategories}
                    filteredExpenseCategories={filteredExpenseCategories}
                    setFilteredExpenseCategories={setFilteredExpenseCategories}
                    allFieldSettings={allFieldSettings}
                    onItemsChange={(updatedItems) => setItems(updatedItems)}
                    onFormDataChange={(e) => {
                      const { name, value } = e.target;
                      setFormData(prev => ({ ...prev, [name]: value }));
                    }}
                  />
                )}
              </>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField select fullWidth label="Site *" name="siteId" value={formData.siteId} onChange={handleChange} required>
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>{site.siteName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {materialType === 'received' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Department *" name="departmentId" value={formData.departmentId} onChange={handleChange} required>
                        {departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>{dept.department_name}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Expense Category *" name="expenseCategoryId"
                        value={formData.expenseCategoryId} onChange={handleChange} required
                        disabled={!formData.departmentId}
                        helperText={!formData.departmentId ? 'Select department first' : ''}>
                        {filteredExpenseCategories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>{cat.category_name}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {!isEmployeeExpenseForm && renderMaterialItemField()}
                    {!isEmployeeExpenseForm && (
                      <>
                        <Grid item xs={8}>
                          <TextField fullWidth type="number"
                            label={currentFieldSettings.quantityRequired ? 'Quantity *' : 'Quantity'}
                            name="quantity" value={formData.quantity} onChange={handleChange}
                            required={currentFieldSettings.quantityRequired}
                            inputProps={{ min: 0, step: 0.01 }} />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField fullWidth
                            label={currentFieldSettings.unitRequired ? 'Unit *' : 'Unit'}
                            name="unit" value={formData.unit} onChange={handleChange}
                            required={currentFieldSettings.unitRequired}
                            InputProps={{ readOnly: !!formData.unit && !!formData.materialItemId }}
                            helperText={!formData.unit && formData.materialItemId ? 'Enter unit manually' : ''} />
                        </Grid>
                      </>
                    )}
                  </>
                )}

                {materialType === 'received' ? (
                  isEmployeeExpenseForm ? (
                    <>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Supplier Name *" name="employeeName"
                          value={formData.employeeName} onChange={handleChange} required />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth type="number" label="Amount of Expenses *"
                          name="amount" value={formData.amount} onChange={handleChange} required
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}
                          color={billFile ? 'success' : 'inherit'}>
                          {billFile ? billFile.name : 'Upload Invoice (Optional)'}
                          <input type="file" hidden accept="image/*,.pdf" onChange={(e) => handleFileChange(e, 'bill')} />
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Payment Source *</FormLabel>
                          <RadioGroup row name="paymentSource" value={formData.paymentSource} onChange={handleChange}>
                            <FormControlLabel value="COMPANY" control={<Radio />} label="Company" />
                            <FormControlLabel value="SUPERVISOR" control={<Radio />} label="Supervisor" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      {formData.paymentSource === 'SUPERVISOR' && (
                        <Grid item xs={12}>
                          <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}>
                            {paymentProofFile ? paymentProofFile.name : 'Upload Payment Proof *'}
                            <input type="file" hidden accept="image/*,.pdf" onChange={(e) => handleFileChange(e, 'payment')} required />
                          </Button>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={2} label="Remarks (Optional)"
                          name="remarks" value={formData.remarks} onChange={handleChange} />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Autocomplete
                          freeSolo
                          options={allVendorNamesForEntry}
                          value={formData.supplierName}
                          onInputChange={(e, newValue) => setFormData(prev => ({ ...prev, supplierName: newValue }))}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth
                              label={currentFieldSettings.supplierNameRequired ? 'Supplier Name *' : 'Supplier Name'}
                              required={currentFieldSettings.supplierNameRequired}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Bill Status *</FormLabel>
                          <RadioGroup row name="billStatus" value={formData.billStatus} onChange={handleChange}>
                            <FormControlLabel value="received" control={<Radio />} label="Bill Received" />
                            <FormControlLabel value="pending" control={<Radio />} label="Bill Pending" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      {formData.billStatus === 'pending' && (
                        <Grid item xs={12}>
                          <TextField fullWidth label="Pending From *" name="billPendingFrom"
                            value={formData.billPendingFrom} onChange={handleChange} required
                            placeholder="e.g. Vendor, Supervisor, Worker, Company..."
                            helperText="Who is this bill pending from?" />
                        </Grid>
                      )}
                      {formData.billStatus === 'received' && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth
                              label={currentFieldSettings.billNumberRequired ? 'Bill Number *' : 'Bill Number'}
                              name="billNumber" value={formData.billNumber} onChange={handleChange}
                              required={currentFieldSettings.billNumberRequired} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">Bill Type *</FormLabel>
                              <RadioGroup row name="billType" value={formData.billType} onChange={handleChange}>
                                <FormControlLabel value="GST" control={<Radio />} label="GST Bill" />
                                <FormControlLabel value="NON_GST" control={<Radio />} label="Non-GST Bill" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={formData.billType === 'GST' && formData.billStatus === 'received' ? 6 : 12}>
                        <TextField fullWidth type="number"
                          label={currentFieldSettings.amountRequired
                            ? (formData.billType === 'GST' ? 'Base Amount (before GST) *' : 'Amount *')
                            : (formData.billType === 'GST' ? 'Base Amount (before GST)' : 'Amount')}
                          name="amount" value={formData.amount} onChange={handleChange}
                          required={currentFieldSettings.amountRequired}
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                      </Grid>
                      {formData.billType === 'GST' && formData.billStatus === 'received' && (
                        <Grid item xs={12} sm={6}>
                          <TextField select fullWidth
                            label={currentFieldSettings.gstSlabRequired ? 'GST Slab *' : 'GST Slab'}
                            name="gstPercentage" value={formData.gstPercentage} onChange={handleChange}
                            required={currentFieldSettings.gstSlabRequired}>
                            {[5, 12, 18, 28].map(slab => (
                              <MenuItem key={slab} value={slab}>{slab}%</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                      {formData.billType === 'GST' && formData.billStatus === 'received' && formData.amount && formData.gstPercentage && (
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>GST Breakdown</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">Base Amount</Typography>
                              <Typography variant="body2">₹{parseFloat(formData.amount).toLocaleString()}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">GST ({formData.gstPercentage}%)</Typography>
                              <Typography variant="body2" color="text.secondary">
                                + ₹{parseFloat((parseFloat(formData.amount) * parseFloat(formData.gstPercentage) / 100).toFixed(2)).toLocaleString()}
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle1" fontWeight="bold">Total Amount (Saved)</Typography>
                              <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                                ₹{parseFloat((parseFloat(formData.amount) * (1 + parseFloat(formData.gstPercentage) / 100)).toFixed(2)).toLocaleString()}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      )}
                      {formData.billStatus === 'received' && (
                        <Grid item xs={12}>
                          <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}
                            color={billFile ? 'success' : 'inherit'}>
                            {billFile ? billFile.name : `Upload Bill (Image/PDF)${currentFieldSettings.billUploadRequired ? ' *' : ''}`}
                            <input type="file" hidden accept="image/*,.pdf"
                              onChange={(e) => handleFileChange(e, 'bill')}
                              required={currentFieldSettings.billUploadRequired && !billFile} />
                          </Button>
                          {billFile && (
                            <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                              ✅ Bill attached
                            </Typography>
                          )}
                        </Grid>
                      )}
                      {formData.billStatus === 'pending' && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            Bill Number, Bill Type, GST, and Bill Upload can be filled in later from the Pending Bills tab once the bill is received.
                          </Alert>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}
                          color={eWayBillFile ? 'success' : 'inherit'}>
                          {eWayBillFile ? eWayBillFile.name : 'Upload E-Way Bill (Optional)'}
                          <input type="file" hidden accept="image/*,.pdf"
                            onChange={(e) => handleFileChange(e, 'eWayBill')} />
                        </Button>
                        {eWayBillFile && (
                          <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                            ✅ E-Way Bill attached
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Payment Source *</FormLabel>
                          <RadioGroup row name="paymentSource" value={formData.paymentSource} onChange={handleChange}>
                            <FormControlLabel value="COMPANY" control={<Radio />} label="Company" />
                            <FormControlLabel value="SUPERVISOR" control={<Radio />} label="Supervisor" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      {formData.paymentSource === 'SUPERVISOR' && (
                        <Grid item xs={12}>
                          <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}>
                            {paymentProofFile ? paymentProofFile.name : 'Upload Payment Proof *'}
                            <input type="file" hidden accept="image/*,.pdf"
                              onChange={(e) => handleFileChange(e, 'payment')} required />
                          </Button>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={2} label="Remarks (Optional)"
                          name="remarks" value={formData.remarks} onChange={handleChange}
                          placeholder="Any additional notes..." />
                      </Grid>
                    </>
                  )
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        options={siteInventory.filter(i => i.currentStock > 0)}
                        getOptionLabel={(option) => `${option.materialName} (Available: ${option.currentStock} ${option.unit})`}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            setFormData(prev => ({
                              ...prev,
                              materialItemId: newValue.type === 'item' ? newValue.materialId : '',
                              categoryId: newValue.type === 'category' ? newValue.materialId : '',
                              customItemDescription: newValue.type === 'custom' ? newValue.materialName : '',
                              unit: newValue.unit || ''
                            }));
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Select Material *"
                            helperText="Only showing materials received for this site" />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box>
                              <Typography variant="body2">{option.materialName}</Typography>
                              <Typography variant="caption" color={option.currentStock <= 0 ? 'error' : 'success.main'}>
                                Available: {option.currentStock} {option.unit}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField fullWidth type="number" label="Quantity *"
                        name="quantity" value={formData.quantity} onChange={handleChange}
                        required inputProps={{ min: 0, step: 0.01 }} />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField fullWidth label="Unit *"
                        name="unit" value={formData.unit} onChange={handleChange} required />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={2} label="Usage Description (Optional)"
                        name="usageDescription" value={formData.usageDescription} onChange={handleChange}
                        placeholder="Describe how this material was used..." />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <TextField fullWidth type="date" label="Entry Date *" name="entryDate"
                    value={formData.entryDate} onChange={handleChange}
                    InputLabelProps={{ shrink: true }} required />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {loading ? 'SUBMITTING...' : 'SUBMIT'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Complete Bill Dialog */}
      <Dialog open={completeBillOpen} onClose={() => setCompleteBillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Bill Details</DialogTitle>
        <DialogContent>
          {completeBillEntry && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info">
                {completeBillEntry.category?.categoryName || completeBillEntry.materialItem?.item_name || completeBillEntry.customItemDescription || completeBillEntry.expenseCategory?.category_name || 'Entry'}
                {' — '}Pending from: <strong>{completeBillEntry.billPendingFrom}</strong>
              </Alert>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label={completeBillEntry?.employeeName ? 'Bill Number (Optional)' : 'Bill Number *'}
                value={completeBillData.billNumber || ''}
                onChange={(e) => setCompleteBillData(prev => ({ ...prev, billNumber: e.target.value }))}
                required={!completeBillEntry?.employeeName} />
            </Grid>
            {!completeBillEntry?.employeeName && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Bill Type</FormLabel>
                    <RadioGroup row value={completeBillData.billType || 'GST'}
                      onChange={(e) => setCompleteBillData(prev => ({ ...prev, billType: e.target.value }))}>
                      <FormControlLabel value="GST" control={<Radio />} label="GST Bill" />
                      <FormControlLabel value="NON_GST" control={<Radio />} label="Non-GST Bill" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Amount"
                    value={completeBillData.amount || ''}
                    onChange={(e) => setCompleteBillData(prev => ({ ...prev, amount: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                </Grid>
                {completeBillData.billType === 'GST' && (
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth label="GST Slab"
                      value={completeBillData.gstPercentage || ''}
                      onChange={(e) => setCompleteBillData(prev => ({ ...prev, gstPercentage: e.target.value }))}>
                      {[5, 12, 18, 28].map(slab => (<MenuItem key={slab} value={slab}>{slab}%</MenuItem>))}
                    </TextField>
                  </Grid>
                )}
              </>
            )}
            {completeBillEntry?.employeeName && (
              <Grid item xs={12}>
                <TextField fullWidth type="number" label="Amount"
                  value={completeBillData.amount || ''}
                  onChange={(e) => setCompleteBillData(prev => ({ ...prev, amount: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}
                color={completeBillFile ? 'success' : 'inherit'}>
                {completeBillFile ? completeBillFile.name : `Upload ${completeBillEntry?.employeeName ? 'Invoice' : 'Bill'} (Image/PDF)`}
                <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setCompleteBillFile(e.target.files[0])} />
              </Button>
            </Grid>
            {!completeBillEntry?.employeeName && (
              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}
                  color={completeBillEWayFile ? 'success' : 'inherit'}>
                  {completeBillEWayFile ? completeBillEWayFile.name : 'Upload E-Way Bill (Optional)'}
                  <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setCompleteBillEWayFile(e.target.files[0])} />
                </Button>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteBillOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleCompleteBillSubmit} disabled={completeBillSubmitting}>
            {completeBillSubmitting ? 'Saving...' : 'Mark as Received'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Entry</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Entry Date"
                value={editFormData.entryDate || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, entryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            {editEntry?.entryType === 'received' && (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={materialItems}
                    getOptionLabel={(option) => option.itemName || option.item_name || ''}
                    value={materialItems.find(item => item.id === editFormData.materialItemId) || null}
                    onChange={(event, newValue) => {
                      setEditFormData(prev => ({
                        ...prev,
                        materialItemId: newValue ? newValue.id : null,
                        unit: newValue ? (newValue.unit || prev.unit) : prev.unit
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Material Item" helperText="Type to search..." />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Custom Description (if not in list)"
                    value={editFormData.customItemDescription || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, customItemDescription: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth type="number" label="Quantity"
                    value={editFormData.quantity || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, quantity: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Unit"
                    value={editFormData.unit || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, unit: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Supplier Name"
                    value={editFormData.supplierName || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, supplierName: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Bill Number"
                    value={editFormData.billNumber || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, billNumber: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth select label="Bill Type"
                    value={editFormData.billType || 'NON_GST'}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, billType: e.target.value }))}>
                    <MenuItem value="GST">GST Bill</MenuItem>
                    <MenuItem value="NON_GST">Non-GST Bill</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth type="number" label="Amount"
                    value={editFormData.amount || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, amount: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                </Grid>
                {editFormData.billType === 'GST' && (
                  <Grid item xs={6}>
                    <TextField fullWidth select label="GST Slab"
                      value={editFormData.gstPercentage || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, gstPercentage: e.target.value }))}>
                      {[5, 12, 18, 28].map(slab => (
                        <MenuItem key={slab} value={slab}>{slab}%</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <TextField fullWidth select label="Payment Source"
                    value={editFormData.paymentSource || 'COMPANY'}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, paymentSource: e.target.value }))}>
                    <MenuItem value="COMPANY">Company</MenuItem>
                    <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}>
                    {editFormData.newBillFile ? editFormData.newBillFile.name : 'Replace Bill Attachment (Optional)'}
                    <input type="file" hidden accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setEditFormData(prev => ({ ...prev, newBillFile: file }));
                      }} />
                  </Button>
                  {editEntry?.billAttachment && !editFormData.newBillFile && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Current bill exists — upload new file to replace it
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}
                    color={editFormData.newEWayBillFile ? 'success' : 'inherit'}>
                    {editFormData.newEWayBillFile ? editFormData.newEWayBillFile.name : 'Upload / Replace E-Way Bill (Optional)'}
                    <input type="file" hidden accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setEditFormData(prev => ({ ...prev, newEWayBillFile: file }));
                      }} />
                  </Button>
                  {editEntry?.eWayBill && !editFormData.newEWayBillFile && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      E-Way Bill exists — upload new file to replace it
                    </Typography>
                  )}
                  {editFormData.newEWayBillFile && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                      ✅ E-Way Bill attached
                    </Typography>
                  )}
                </Grid>
              </>
            )}
            {editEntry?.entryType === 'used' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth type="number" label="Quantity"
                    value={editFormData.quantity || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, quantity: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Unit"
                    value={editFormData.unit || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, unit: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Usage Description"
                    value={editFormData.usageDescription || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, usageDescription: e.target.value }))} />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Entry</DialogTitle>
        <DialogContent>
          {entryToDelete && (
            <Box sx={{ mt: 1 }}>
              <Typography><strong>Material:</strong> {entryToDelete.category?.categoryName || entryToDelete.materialItem?.item_name || entryToDelete.customItemDescription || '-'}</Typography>
              <Typography><strong>Amount:</strong> ₹{Number(entryToDelete.amount || 0).toLocaleString()}</Typography>
              <Typography><strong>Date:</strong> {new Date(entryToDelete.entryDate).toLocaleDateString()}</Typography>
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Alert severity="warning">
              <strong>Soft Delete</strong> — Entry moves to Trash. Can be restored later by admin.
            </Alert>
            <Alert severity="error">
              <strong>Hard Delete</strong> — Entry is permanently deleted. Cannot be recovered.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="outlined" color="warning" onClick={() => handleDeleteConfirm('soft')}>
            Soft Delete (Recoverable)
          </Button>
          <Button variant="contained" color="error" onClick={() => handleDeleteConfirm('hard')}>
            Hard Delete (Permanent)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={imageViewerOpen} onClose={handleCloseImageViewer} maxWidth="md" fullWidth>
        <DialogTitle>
          Bill Attachment
          <IconButton onClick={handleCloseImageViewer} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <img src={selectedImage} alt="Bill Attachment"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadImage} startIcon={<DownloadIcon />}>Open in New Tab</Button>
          <Button onClick={handleCloseImageViewer}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Response Dialog */}
      <Dialog open={responseDialogOpen} onClose={handleResponseDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{responseType === 'success' ? '✅ Success' : '❌ Error'}</DialogTitle>
        <DialogContent>
          <Alert severity={responseType === 'success' ? 'success' : 'error'} sx={{ mt: 2 }}>
            {responseMessage}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResponseDialogClose} variant="contained" autoFocus>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: 'column', gap: 2 }}
        open={loading && isSubmitting}>
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Processing...</Typography>
        <Typography variant="body2">Please wait while we save your entry</Typography>
      </Backdrop>
    </Box>
  );
};

export default MaterialsPage;